import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import FullPageSpinner from "@khan/component-screens/full-page-spinner";
import type {ActionBubble as ActionBubbleType} from "@khan/guide-types";
import {stripEmojis} from "@khan/guide-utils";

import {ActionBubbleContent} from "./action-bubble-content.tsx";
import {getActionIcon} from "./get-action-icon.ts";
import {useStringTranslation} from "./use-string-translation.ts";

interface Props {
    actions: ActionBubbleType["actions"];
    disabled?: boolean;
    isOnMostRecentMessage?: boolean;
    label: string;
    oldMessageBehavior?: "disable" | "hide";
    isDynamic?: boolean;
    size?: React.ComponentProps<typeof Button>["size"];
    style?: StyleType;
    wrapText?: boolean;
    variant?: "primary" | "secondary" | "tertiary";
    onActionSelected: () => unknown;
    id?: string;
}

/**
 * This component uses a Khanmigo themed version of the Wonder Blocks Button
 * component. Keep this in mind when making changes to the component.
 */
const ActionBubble = (props: Props): React.ReactElement => {
    const {
        actions,
        disabled,
        isOnMostRecentMessage,
        label,
        oldMessageBehavior,
        isDynamic,
        size,
        style,
        variant = "secondary",
        wrapText = true,
        onActionSelected,
        id,
    } = props;

    const {translation: bubbleText, loading} = useStringTranslation(label);

    // Create a screen reader accessible version by stripping emojis
    const accessibleBubbleText = React.useMemo(
        () => stripEmojis(bubbleText),
        [bubbleText],
    );

    const {icon, placement} = getActionIcon(actions ?? []);
    const {startIcon, endIcon} = React.useMemo(() => {
        if (icon) {
            if (placement === "start") {
                return {startIcon: icon};
            }
            if (placement === "end") {
                return {endIcon: icon};
            }
        }
        return {}; // both undefined
    }, [icon, placement]);

    if (loading) {
        return <FullPageSpinner />;
    }

    if (!isOnMostRecentMessage && oldMessageBehavior === "hide") {
        return <></>;
    }

    const shouldDisable =
        disabled ||
        (!isOnMostRecentMessage && oldMessageBehavior === "disable");

    // NOTE(CLASS-9509): Primary is used for providing higher hierarchy
    // actions in certain contexts. In these cases, we want to use the
    // "progressive" action type to indicate that the action is more important
    // than the default "neutral" action type.
    const actionType = variant === "primary" ? "progressive" : "neutral";

    return (
        // @ts-expect-error currently Button component only supports a string as its child,
        // but we need to display markdown in this case, so ignore the error for now.
        // Approval from FEI: https://khanacademy.slack.com/archives/C0918TZ5G/p1708975682600869?thread_ts=1708970818.318899&cid=C0918TZ5G
        <Button
            actionType={actionType}
            kind={variant}
            disabled={shouldDisable}
            onClick={onActionSelected}
            labelStyle={[styles.label, wrapText ? styles.wrap : styles.nowrap]}
            size={size}
            style={[styles.actionBubble, style]}
            testId="action-bubble"
            startIcon={startIcon}
            endIcon={endIcon}
            id={id}
            aria-label={accessibleBubbleText}
        >
            <span aria-hidden="true">
                <ActionBubbleContent
                    isDynamic={isDynamic}
                    bubbleText={bubbleText}
                />
            </span>
        </Button>
    );
};

const styles = StyleSheet.create({
    actionBubble: {
        alignSelf: "flex-start",
        // Necessary to cause bubbles with long text to elide, otherwise
        // they run off the edge of the viewport.
        maxWidth: "100%",
        // Necessary to cause bubbles with long text to wrap, otherwise
        // the label gets cut off.
        height: "auto",
        minBlockSize: spacing.xLarge_32,
        paddingBlockStart: spacing.xSmall_8,
        paddingBlockEnd: spacing.xSmall_8,
    },
    label: {
        textAlign: "start",
        // Avoid truncating the label text.
        textOverflow: "initial",
        whiteSpace: "normal",
    },
    wrap: {
        whiteSpace: "normal",
    },
    nowrap: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
});

export default ActionBubble;
